/**
 * iOS In-App Purchase Error Handler
 * 
 * PHASE 3.1: Comprehensive Error Handling and Feedback
 * 
 * This utility provides centralized error handling for iOS IAP scenarios
 * with user-friendly messages and actionable guidance.
 * 
 * Features:
 * - Platform-specific error classification
 * - User-friendly error messages
 * - Actionable guidance for error resolution
 * - Retry logic recommendations
 * - Integration with toast notification system
 */

import { toast } from '@/hooks/use-toast';

/**
 * iOS IAP Error Types
 */
export type IOSIAPErrorType = 
  | 'not_available'
  | 'purchase_in_progress'
  | 'user_cancelled'
  | 'payment_invalid'
  | 'product_not_found'
  | 'receipt_validation_failed'
  | 'network_error'
  | 'app_store_unavailable'
  | 'insufficient_permissions'
  | 'family_sharing_conflict'
  | 'subscription_expired'
  | 'unknown_error';

/**
 * Error Classification Result
 */
export interface IOSIAPErrorClassification {
  type: IOSIAPErrorType;
  title: string;
  message: string;
  isRetryable: boolean;
  actionable: boolean;
  suggestedAction?: string;
}

/**
 * Classify iOS IAP errors and provide user-friendly messages
 */
export function classifyIOSIAPError(error: Error | string): IOSIAPErrorClassification {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const lowerMessage = errorMessage.toLowerCase();

  // IAP not available
  if (lowerMessage.includes('iap not available') || 
      lowerMessage.includes('not available on this platform')) {
    return {
      type: 'not_available',
      title: 'App Store Unavailable',
      message: 'App Store purchases are not available on this device. Please check your device settings and try again.',
      isRetryable: true,
      actionable: true,
      suggestedAction: 'Check your device\'s App Store settings and ensure you\'re signed in to your Apple ID.'
    };
  }

  // Purchase already in progress
  if (lowerMessage.includes('purchase already in progress') ||
      lowerMessage.includes('already in progress')) {
    return {
      type: 'purchase_in_progress',
      title: 'Purchase In Progress',
      message: 'A purchase is already in progress. Please wait for it to complete before trying again.',
      isRetryable: true,
      actionable: false
    };
  }

  // User cancelled
  if (lowerMessage.includes('user cancelled') ||
      lowerMessage.includes('cancelled by user') ||
      lowerMessage.includes('user canceled')) {
    return {
      type: 'user_cancelled',
      title: 'Purchase Cancelled',
      message: 'The purchase was cancelled. You can try again when you\'re ready.',
      isRetryable: true,
      actionable: false
    };
  }

  // Payment invalid
  if (lowerMessage.includes('payment invalid') ||
      lowerMessage.includes('invalid payment') ||
      lowerMessage.includes('payment method')) {
    return {
      type: 'payment_invalid',
      title: 'Payment Issue',
      message: 'There was an issue with your payment method. Please check your App Store payment settings.',
      isRetryable: true,
      actionable: true,
      suggestedAction: 'Update your payment method in App Store settings and try again.'
    };
  }

  // Product not found
  if (lowerMessage.includes('product not found') ||
      lowerMessage.includes('no product found') ||
      lowerMessage.includes('product unavailable')) {
    return {
      type: 'product_not_found',
      title: 'Product Unavailable',
      message: 'The subscription product is temporarily unavailable. Please try again later.',
      isRetryable: true,
      actionable: false
    };
  }

  // Receipt validation failed
  if (lowerMessage.includes('receipt validation') ||
      lowerMessage.includes('invalid receipt') ||
      lowerMessage.includes('receipt failed')) {
    return {
      type: 'receipt_validation_failed',
      title: 'Verification Issue',
      message: 'There was an issue verifying your purchase. Your payment is safe, and we\'ll resolve this automatically.',
      isRetryable: false,
      actionable: true,
      suggestedAction: 'If the issue persists, please contact support with your purchase details.'
    };
  }

  // Network error
  if (lowerMessage.includes('network') ||
      lowerMessage.includes('connection') ||
      lowerMessage.includes('timeout') ||
      lowerMessage.includes('offline')) {
    return {
      type: 'network_error',
      title: 'Connection Issue',
      message: 'Unable to connect to the App Store. Please check your internet connection and try again.',
      isRetryable: true,
      actionable: true,
      suggestedAction: 'Check your internet connection and try again.'
    };
  }

  // App Store unavailable
  if (lowerMessage.includes('app store') ||
      lowerMessage.includes('store unavailable') ||
      lowerMessage.includes('service unavailable')) {
    return {
      type: 'app_store_unavailable',
      title: 'App Store Unavailable',
      message: 'The App Store is temporarily unavailable. Please try again in a few minutes.',
      isRetryable: true,
      actionable: false
    };
  }

  // Insufficient permissions
  if (lowerMessage.includes('permission') ||
      lowerMessage.includes('not authorized') ||
      lowerMessage.includes('access denied')) {
    return {
      type: 'insufficient_permissions',
      title: 'Permission Required',
      message: 'App Store purchases require additional permissions. Please check your device settings.',
      isRetryable: true,
      actionable: true,
      suggestedAction: 'Enable App Store purchases in your device\'s Screen Time or Restrictions settings.'
    };
  }

  // Family sharing conflict
  if (lowerMessage.includes('family sharing') ||
      lowerMessage.includes('family') ||
      lowerMessage.includes('organizer')) {
    return {
      type: 'family_sharing_conflict',
      title: 'Family Sharing Issue',
      message: 'There\'s an issue with Family Sharing. Please check with your family organizer or manage subscriptions directly.',
      isRetryable: true,
      actionable: true,
      suggestedAction: 'Contact your Family Sharing organizer or manage the subscription in App Store settings.'
    };
  }

  // Subscription expired
  if (lowerMessage.includes('expired') ||
      lowerMessage.includes('subscription ended') ||
      lowerMessage.includes('no longer active')) {
    return {
      type: 'subscription_expired',
      title: 'Subscription Expired',
      message: 'Your subscription has expired. You can renew it to continue enjoying premium features.',
      isRetryable: true,
      actionable: true,
      suggestedAction: 'Renew your subscription to continue using premium features.'
    };
  }

  // Unknown error
  return {
    type: 'unknown_error',
    title: 'Unexpected Error',
    message: 'An unexpected error occurred. Please try again or contact support if the issue persists.',
    isRetryable: true,
    actionable: true,
    suggestedAction: 'Try again in a few moments, or contact support if the problem continues.'
  };
}

/**
 * Handle iOS IAP errors with user-friendly feedback
 */
export function handleIOSIAPError(
  error: Error | string,
  context: string = 'iOS IAP Operation'
): IOSIAPErrorClassification {
  const classification = classifyIOSIAPError(error);
  
  console.error(`❌ ${context}: ${classification.type}`, {
    originalError: error,
    classification
  });

  // Show toast notification with appropriate variant
  const variant = classification.isRetryable ? 'destructive' : 'default';
  
  toast({
    title: classification.title,
    description: classification.message,
    variant
  });

  return classification;
}

/**
 * Handle iOS IAP errors with retry logic
 */
export function handleIOSIAPErrorWithRetry(
  error: Error | string,
  retryFunction: () => Promise<void>,
  maxRetries: number = 3,
  context: string = 'iOS IAP Operation'
): IOSIAPErrorClassification {
  const classification = handleIOSIAPError(error, context);
  
  // If error is retryable and we haven't exceeded max retries
  if (classification.isRetryable && maxRetries > 0) {
    console.log(`🔄 ${context}: Attempting retry (${maxRetries} attempts remaining)`);
    
    // Add retry button to toast if actionable
    if (classification.actionable) {
      toast({
        title: `${classification.title} - Retry Available`,
        description: `${classification.message} Tap to retry.`,
        variant: 'default',
        action: {
          altText: 'Retry',
          onClick: () => {
            setTimeout(() => {
              retryFunction().catch((retryError) => {
                handleIOSIAPErrorWithRetry(retryError, retryFunction, maxRetries - 1, context);
              });
            }, 1000); // Wait 1 second before retry
          }
        }
      });
    }
  }

  return classification;
}

/**
 * Get user-friendly error message for display in UI components
 */
export function getIOSIAPErrorMessage(error: Error | string): string {
  const classification = classifyIOSIAPError(error);
  return classification.message;
}

/**
 * Check if an error is retryable
 */
export function isIOSIAPErrorRetryable(error: Error | string): boolean {
  const classification = classifyIOSIAPError(error);
  return classification.isRetryable;
}

/**
 * Get suggested action for an error
 */
export function getIOSIAPErrorAction(error: Error | string): string | undefined {
  const classification = classifyIOSIAPError(error);
  return classification.suggestedAction;
}
